<script setup lang="ts">
import { PropertyId } from "~/classes/entity"

const props = defineProps({
  fileId: null,
  open: { type: Boolean },
  parent: { type: Boolean }
})

const { isSelectedIn } = useSelection()
const selected = computed(() => isSelectedIn(props.fileId, "collection-view"))
const { state: viewModeState } = useViewMode()
const { fileIdAttr } = useGlobals()
const { getReactiveFile } = useCollectionView()

const reactiveFile = getReactiveFile(props.fileId, "collection-view")!

if (!reactiveFile)
  throw new Error("Must not instantiate CollectionColumnFile without a reactive file")

const file = reactiveFile.ref()

const properties = computed(
  () => viewModeState
    .value
    .current
    .show
    .filter(p => p.propertyId !== PropertyId.FILE_VIRTUAL_PREVIEW)
    .filter(p => file.value.hasProperty(p.propertyId)),
)
</script>

<template>
  <div
    class="column-item-container"
    :class="{ selected, open, parent }"
    :[dataAttr(fileIdAttr)]="fileId"
  >
    <div
      :style="{
        width: px(20),
        height: px(20),
      }"
      class="column-file-icon"
    >
      <file-thumbnail
        :file
        :container-width="20"
        :container-height="20"
        shadowed
      />
    </div>
    <div class="column-properties">
      <file-property-view
        v-for="{ propertyId, size, color } in properties"
        :key="propertyId"
        :property-id
        :size
        :reactive-file
        :color
        :selected
        text-alignment="left"
      />
    </div>
    <div class="column-file-status">
      <file-decal
        :file
        :selected
        class="column-file-decal"
      />
      <div class="column-folder-chevron">
        <icon-action
          v-if="file.isFolder()"
          size="2xs"
          type="chevron-right"
        />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.column-item-container
  @apply flex items-center gap-[#{$page-column-gap-item}] overflow-hidden cursor-pointer -m-1 p-1 rounded-[#{$page-column-br-file}] h-8
  &:hover
    @apply bg-[#{$page-column-bg-item-hover}]
  &.selected
    @apply bg-$global-blue-primary

.column-properties
  @apply flex items-center min-w-0 text-ellipses-inline
.column-file-icon
  @apply flex-none flex items-center justify-center
.column-file-text
  @apply text-ellipses-inline

.column-file-status
  @apply ml-auto flex-none flex items-center
.column-file-decal
  @apply flex-none

.column-folder-chevron
  @apply h-5 w-2.5 text-[#{$global-bg-icon-secondary}] flex-none items-center flex flex-none justify-center

.selected
  .column-file-icon
    @apply text-[#{$global-t-default}]
  .column-folder-chevron
    @apply text-[#{$global-t-default}]
</style>
